import { useEffect, useState } from 'react';
import { WEB_ICO } from './icons';
import { WebModal } from './WebModal';
import { useViewport } from './useViewport';
import { ErrorState, LoadingState } from '../components/ScreenState';
import { useAsync } from '../hooks/useAsync';
import { contentApi } from '../api/content';
import { progressApi } from '../api/progress';
import type { ExerciseSection } from '../api/content.types';
import type { ExerciseAttemptList, ExerciseAttemptSummary } from '../api/progress.types';
import { WebExerciseHistoryList } from './WebExerciseHistoryList';
import { retryRange } from './exerciseHistoryFormat';

type DialogTab = 'practice' | 'history';

/// Mỗi Unit có đúng 3 bộ đề — khớp seed (packages/database/prisma/seed/data/exercise-sets).
const EXERCISE_SET_COUNT = 3;

const SECTION_META: Record<ExerciseSection, { jp: string; vi: string }> = {
  VOCAB: { jp: '文字・語彙', vi: 'Từ vựng' },
  GRAMMAR: { jp: '文法', vi: 'Ngữ pháp' },
  READING: { jp: '読解', vi: 'Đọc hiểu' },
};
const SECTION_ORDER: ExerciseSection[] = ['VOCAB', 'GRAMMAR', 'READING'];

export interface WebExerciseDialogProps {
  onClose: () => void;
  /** Unit đang mở (node vừa bấm trên Lộ trình) — CỐ ĐỊNH, dialog không đổi sang Unit khác. */
  unitId: string;
  baiNumber: number;
  onStart: (setNumber: number, lo: number, hi: number) => void;
}

/**
 * Dialog riêng cho node "Bài tập" trên Lộ trình — đề dạng JLPT N5 thật (API
 * `/units/:unitId/exercise-sets/:setNumber`). Mỗi Unit có 3 BỘ ĐỀ riêng
 * ("Bài 1/2/3" ở tab — 3 bộ đề CỦA Unit này, không phải 3 Unit khác nhau), mỗi
 * bộ cấu trúc cố định 3 phần: Từ vựng 5 câu / Ngữ pháp 5 câu / Đọc hiểu 2 câu
 * = 12 câu. Tab "Lịch sử" liệt kê các lượt đã nộp của bộ đề đang chọn.
 * Khớp mobile ExerciseSheet.tsx.
 */
export function WebExerciseDialog({ onClose, unitId, baiNumber, onStart }: WebExerciseDialogProps) {
  const { phone } = useViewport();
  const [selectedSet, setSelectedSet] = useState(1);
  const [tab, setTab] = useState<DialogTab>('practice');
  useEffect(() => {
    setSelectedSet(1);
    setTab('practice');
  }, [unitId]);

  const setNumbers = Array.from({ length: EXERCISE_SET_COUNT }, (_, i) => i + 1);

  // Tải CẢ 3 bộ đề một lần khi mở dialog (mỗi bộ chỉ 12 câu, rất nhẹ) — chuyển
  // tab sau đó chỉ là đổi index cục bộ, không gọi lại API, không còn giật/nháy
  // loading khi bấm "Bài 2/3" như khi tải từng bộ theo tab đang chọn.
  const exerciseSets = useAsync(() => Promise.all(setNumbers.map((n) => contentApi.getExerciseSet(unitId, n))), [unitId]);
  const current = exerciseSets.data?.[selectedSet - 1];

  // Lịch sử của cả 3 bộ đề tải cùng lúc ngay sau khi có đề — để "Lịch sử (N)"
  // có số ngay. Dialog bị unmount khi đóng/bắt đầu làm bài nên lần mở sau luôn
  // tải lại, lượt vừa nộp tự hiện ra.
  const history = useAsync(
    async () => (exerciseSets.data ? Promise.all(exerciseSets.data.map((set) => progressApi.listExerciseAttempts(set.id))) : null),
    [exerciseSets.data],
  );
  // Trang "Xem thêm" đã nối thêm, gắn với đúng lần tải `history.data` đó.
  const [extraPages, setExtraPages] = useState<{ base: unknown; lists: Record<number, ExerciseAttemptList> }>({ base: null, lists: {} });
  const [loadingMore, setLoadingMore] = useState(false);
  const extraLists = extraPages.base === history.data ? extraPages.lists : {};
  const currentHistory = extraLists[selectedSet] ?? history.data?.[selectedSet - 1];
  const historyCount = history.data?.[selectedSet - 1]?.totalAttempts;

  const loadMoreHistory = async () => {
    if (!current || !currentHistory?.nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await progressApi.listExerciseAttempts(current.id, currentHistory.nextCursor);
      setExtraPages({ base: history.data, lists: { ...extraLists, [selectedSet]: { ...next, items: [...currentHistory.items, ...next.items] } } });
    } catch {
      // Giữ nguyên danh sách đang có — bấm "Xem thêm" lần nữa để thử lại.
    } finally {
      setLoadingMore(false);
    }
  };

  const retryAttempt = (attempt: ExerciseAttemptSummary) => {
    const range = current && retryRange(current.questions, attempt.sectionScores.map((s) => s.section));
    if (range) onStart(selectedSet, range.lo, range.hi);
  };

  const sectionCounts: Partial<Record<ExerciseSection, number>> = {};
  for (const q of current?.questions ?? []) {
    sectionCounts[q.section] = (sectionCounts[q.section] ?? 0) + 1;
  }
  const totalCau = current?.questions.length ?? 0;

  const startSection = (section: ExerciseSection) => {
    const qs = current?.questions ?? [];
    const indices = qs.map((q, i) => (q.section === section ? i : -1)).filter((i) => i >= 0);
    if (indices.length === 0) return;
    onStart(selectedSet, indices[0], indices[indices.length - 1] + 1);
  };

  return (
    <WebModal onClose={onClose} width={880}>
      {/* Điện thoại: 2 cột xếp chồng, cả dialog cuộn chung thay vì cuộn riêng cột phải. */}
      <div
        style={{
          display: 'flex',
          flexDirection: phone ? 'column' : 'row',
          minHeight: 0,
          maxHeight: phone ? '92dvh' : '88vh',
          overflowY: phone ? 'auto' : undefined,
          fontFamily: 'var(--font-vi)',
        }}
      >
        <div
          style={{
            width: phone ? undefined : 288,
            flexShrink: 0,
            borderRight: phone ? 'none' : '1px solid var(--border-default)',
            borderBottom: phone ? '1px solid var(--border-default)' : 'none',
            background: 'var(--surface-tint)',
            padding: phone ? 16 : 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--navy-300)' }}>
            {`Bài tập · Bài tập ${selectedSet}`}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span
              style={{
                width: 60,
                height: 60,
                borderRadius: 16,
                background: 'var(--surface-page)',
                border: '1px solid var(--border-default)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'var(--font-jp)',
                fontSize: 28,
                color: 'var(--cta-default)',
                flexShrink: 0,
              }}
            >
              練
            </span>
            <h2 style={{ fontSize: 21, fontWeight: 700, color: 'var(--ink)', margin: 0, lineHeight: 1.25 }}>{`Unit ${baiNumber}`}</h2>
          </div>

          <div style={{ display: 'flex', gap: 4, background: 'var(--surface-page)', border: '1px solid var(--border-default)', borderRadius: 999, padding: 4 }}>
            {setNumbers.map((n) => {
              const active = n === selectedSet;
              return (
                <button
                  key={n}
                  onClick={() => setSelectedSet(n)}
                  style={{
                    flex: 1,
                    height: 36,
                    borderRadius: 999,
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-vi)',
                    fontSize: 13,
                    fontWeight: 700,
                    background: active ? 'var(--cta-default)' : 'transparent',
                    color: active ? '#fff' : 'var(--text-secondary)',
                    transition: 'background 150ms ease, color 150ms ease',
                  }}
                >
                  {`Bài ${n}`}
                </button>
              );
            })}
          </div>

          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>Học theo thứ tự hoặc chọn bài bất kỳ. Sai không bị trừ điểm — bạn cứ thử.</div>
          {!phone && <div style={{ flex: 1 }} />}
          <button
            onClick={() => totalCau > 0 && onStart(selectedSet, 0, totalCau)}
            disabled={totalCau === 0}
            style={{
              height: 50,
              borderRadius: 'var(--radius-control)',
              background: totalCau > 0 ? 'var(--cta-default)' : 'var(--border-default)',
              color: totalCau > 0 ? '#fff' : 'var(--text-secondary)',
              border: 'none',
              cursor: totalCau > 0 ? 'pointer' : 'default',
              fontFamily: 'var(--font-vi)',
              fontWeight: 700,
              fontSize: 15.5,
            }}
          >
            {`Làm hết ${totalCau} câu`}
          </button>
          <button
            onClick={onClose}
            style={{
              height: 44,
              borderRadius: 'var(--radius-control)',
              background: 'transparent',
              border: '1px solid var(--border-default)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontFamily: 'var(--font-vi)',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Đóng
          </button>
        </div>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div role="tablist" style={{ display: 'flex', gap: 24, padding: '8px 24px 0', borderBottom: '1px solid var(--border-default)' }}>
            {(
              [
                { key: 'practice', label: `Làm bài · ${SECTION_ORDER.length} phần` },
                { key: 'history', label: historyCount == null ? 'Lịch sử' : `Lịch sử (${historyCount})` },
              ] as const
            ).map((t) => {
              const active = t.key === tab;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(t.key)}
                  style={{
                    background: 'none',
                    border: 'none',
                    borderBottom: `2.5px solid ${active ? 'var(--cta-default)' : 'transparent'}`,
                    marginBottom: -1,
                    padding: '10px 2px 12px',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-vi)',
                    fontSize: 14,
                    fontWeight: 700,
                    color: active ? 'var(--cta-default)' : 'var(--text-secondary)',
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            {exerciseSets.loading ? (
              <div style={{ padding: 24 }}>
                <LoadingState />
              </div>
            ) : exerciseSets.error || !exerciseSets.data ? (
              <ErrorState message="Không tải được đề bài tập." />
            ) : tab === 'history' ? (
              <div key={`history-${selectedSet}`} style={{ animation: 'torii-tab-content-in 200ms ease' }}>
                {history.loading ? (
                  <LoadingState />
                ) : history.error || !currentHistory ? (
                  <ErrorState message="Không tải được lịch sử làm bài." />
                ) : (
                  <WebExerciseHistoryList
                    attempts={currentHistory.items}
                    hasMore={currentHistory.nextCursor != null}
                    loadingMore={loadingMore}
                    onLoadMore={loadMoreHistory}
                    onRetry={retryAttempt}
                  />
                )}
              </div>
            ) : (
              // `key={selectedSet}` buộc React tạo node DOM mới mỗi lần đổi tab,
              // nên animation CSS (vốn chỉ chạy khi phần tử được tạo) tự chạy lại.
              <div key={selectedSet} style={{ display: 'flex', flexDirection: 'column', gap: 8, animation: 'torii-tab-content-in 200ms ease' }}>
                {SECTION_ORDER.map((section) => {
                  const meta = SECTION_META[section];
                  const count = sectionCounts[section] ?? 0;
                  return (
                    <button
                      key={section}
                      onClick={() => startSection(section)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontFamily: 'var(--font-vi)',
                        padding: '12px 14px',
                        background: 'var(--surface-page)',
                        border: '1px solid var(--border-default)',
                        borderRadius: 'var(--radius-card)',
                      }}
                    >
                      <span
                        style={{
                          width: 44,
                          height: 44,
                          flexShrink: 0,
                          borderRadius: '50%',
                          background: 'var(--surface-tint)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 700,
                          color: 'var(--cta-default)',
                          fontFamily: 'var(--font-jp, sans-serif)',
                        }}
                      >
                        {meta.jp.slice(0, 2)}
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{meta.vi}</span>
                        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{meta.jp}</span>
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{`${count} câu`}</span>
                      {WEB_ICO.chev()}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </WebModal>
  );
}
